<script setup lang="ts">
import { ROOT_URL_HOST } from '@/shared/constants/contactConstants';
import AppBreadcrumbs from '@/shared/ui/AppBreadcrumbs/index';
import SchemaOrg from '@/shared/ui/SchemaOrg/index';
import {
  buildBreadcrumbData,
  type BreadcrumbSchemaData,
} from '@/shared/utils/buildSchemaData';
import { scrollToTop } from '@/shared/utils/scrollBehavior';
import PoliciesTabs from '@/pages/policies/ui/PoliciesTabs.vue';
import TermsPolicy from '@/pages/policies/ui/documents/TermsPolicy.vue';
import PrivacyPolicy from '@/pages/policies/ui/documents/PrivacyPolicy.vue';
import UsersConsentPolicy from '@/pages/policies/ui/documents/UsersConsentPolicy.vue';
import ClientsConsentPolicy from '@/pages/policies/ui/documents/ClientsConsentPolicy.vue';
import OfferPolicy from '@/pages/policies/ui/documents/OfferPolicy.vue';
import DistributionConsentPolicy from '@/pages/policies/ui/documents/DistributionConsentPolicy.vue';
import { usePolicies } from '@/pages/policies/composables/usePolicies';
import { POLICY_DOCUMENTS, LEGAL_OG_TITLE } from '@/pages/policies/lib/policiesDocuments';

const isMobile: boolean = useIsMobile();
const route = useRoute();

const {
  slug,
  activeDocument,
  metaTitle,
  metaDescription,
  breadcrumbs,
  isShowScrollButton,
} = usePolicies();

const documentComponents: Record<string, Component> = {
  terms: TermsPolicy,
  'privacy-policy': PrivacyPolicy,
  'consent-form-1': UsersConsentPolicy,
  'consent-form-2': ClientsConsentPolicy,
  offer: OfferPolicy,
  'consent-distribution': DistributionConsentPolicy,
};

const activeDocumentComponent = computed<Component | null>(() =>
  activeDocument.value ? (documentComponents[activeDocument.value.slug] ?? null) : null,
);

const url = computed(() => `${ROOT_URL_HOST}${route.path}`);

const breadcrumbSchemaData = computed<BreadcrumbSchemaData>(() =>
  buildBreadcrumbData(
    breadcrumbs.value.map((breadcrumb) => ({
      name: breadcrumb.text,
      url: breadcrumb.to ?? route.path,
    })),
  ),
);

const webPageData = computed<Record<string, unknown>>(() => ({
  name: metaTitle.value,
  description: metaDescription.value,
  url: url.value,
}));

useSeoMeta({
  title: metaTitle,
  description: metaDescription,
  ogTitle: LEGAL_OG_TITLE,
  ogDescription: metaDescription,
  ogSiteName: 'Сервис по подбору юристов, адвокатов - Юрик.ру',
  ogType: 'website',
  ogUrl: url,
  ogLocale: 'ru',
  ogImage: `${ROOT_URL_HOST}/og-image.png`,
});

useHead({
  meta: [{ property: 'vk:image', content: `${ROOT_URL_HOST}/og-image.png` }],
});
</script>

<template>
  <div :class="['policies-page', { _mobile: isMobile }]" data-testid="policies-page">
    <SchemaOrg type="BreadcrumbList" :data="breadcrumbSchemaData" />
    <SchemaOrg type="WebPage" :data="webPageData" />
    <div class="policies-page__breadcrumbs">
      <AppBreadcrumbs :breadcrumbs="breadcrumbs" />
    </div>
    <h1 class="policies-page__title">
      Правовые документы
      <div v-if="!isMobile" class="policies-page__icon-wrapper">
        <AppIcon name="Policies" class="policies-page__icon" />
      </div>
    </h1>
    <div class="policies-page__tabs-wrapper">
      <PoliciesTabs :tab-list="POLICY_DOCUMENTS" :active-slug="slug" />
      <component :is="activeDocumentComponent" v-if="activeDocumentComponent" />
    </div>
    <button
      :class="['policies-page__button', { _show: isShowScrollButton }]"
      type="button"
      aria-label="Прокрутить наверх"
      data-testid="policies-scroll-top"
      @click="scrollToTop()"
    >
      <AppIcon name="ArrowUpCircle" />
    </button>
  </div>
</template>

<style lang="less">
.policies-page {
  .container-d();
  .flex-column(@size-x8);

  position: relative;
  padding-bottom: 100px;

  &__breadcrumbs {
    margin-top: @size-x10;
  }

  &__title {
    .h-offset-reset();

    font-size: @size-x8;
    font-weight: 700;
    line-height: 130%;
    position: relative;
  }

  &__icon-wrapper {
    position: absolute;
    right: 0;
    bottom: -@size-x8;
  }

  &__tabs-wrapper {
    display: flex;
    flex-direction: column;
    gap: 80px;
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    right: 120px;
    bottom: @size-x10;
    z-index: -1;
    opacity: 0;
    pointer-events: none;
    transition: opacity @A ease-in-out;

    &._show {
      opacity: 1;
      pointer-events: auto;
      z-index: 999;
    }
  }

  &._mobile {
    .container-m();
    .flex-column(@size-x6);
  }

  &._mobile &__breadcrumbs {
    margin-top: @size-x8;
  }

  &._mobile &__title {
    font-size: @size-x6;
  }

  &._mobile &__tabs-wrapper {
    gap: @size-x10;
  }

  &._mobile &__button {
    right: @size-x5;
    bottom: 90px;
  }
}
</style>
